// src/ClinicalDataCollector/components/output/MedicationsSummary.jsx
import React, { useContext, useEffect, useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Chip,
  Stack,
  Alert,
} from '@mui/material';
import MedicationIcon from '@mui/icons-material/Medication';
import ScheduleIcon from '@mui/icons-material/Schedule';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSelector } from 'react-redux';

import { AdvancedMdContext } from '../../../../context/AdvancedMD/AdvancedMdContext';
import { formatDate as formatDateUS } from '../../../../utils/formatters/date';

const MedicationsSummary = () => {
  // 🔹 id AdvancedMD desde Redux
  const { advancedMdPatientId } = useSelector(
    state => state.patientMapSelect
  );

  // 🔹 contexto AdvancedMD
  const {
    medications,
    medicationsLoading,
    medicationsError,
    loadMedications,
  } = useContext(AdvancedMdContext);

  // 🔹 cargar meds al tener patientId
  useEffect(() => {
    if (!advancedMdPatientId) return;
    loadMedications(advancedMdPatientId);
  }, [advancedMdPatientId, loadMedications]);

  // 🔹 separar meds actuales vs históricos
  const { currentMeds, historicMeds } = useMemo(() => {
    const list = Array.isArray(medications) ? medications : [];

    const currents = list.filter(m => {
      const status = (m.status || '').toLowerCase();
      return m.currentMedFlag || status.startsWith('current');
    });

    const currentIds = new Set(currents.map(m => m.id));
    const historic = list.filter(m => !currentIds.has(m.id));

    return { currentMeds: currents, historicMeds: historic };
  }, [medications]);

  const formatDate = value => formatDateUS(value, null);

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
      <Typography
        variant="h6"
        sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
      >
        <MedicationIcon color="info" />
        <Box ml={1}>Medications (AdvancedMD)</Box>
      </Typography>

      {medicationsLoading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Loading medications from AdvancedMD…
        </Typography>
      )}

      {medicationsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading medications:{' '}
          {medicationsError.message || 'Unknown error'}
        </Alert>
      )}

      {!medicationsLoading &&
        !medicationsError &&
        (!medications || medications.length === 0) && (
          <Typography variant="body2" color="text.secondary">
            No medications found for this patient.
          </Typography>
        )}

      {/* ========= MEDS ACTUALES ========= */}
      {currentMeds.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 1,
              fontWeight: 600,
            }}
          >
            <CheckCircleIcon color="success" fontSize="small" />
            <Box ml={1}>Current medications</Box>
          </Typography>

          <Grid container spacing={1}>
            {currentMeds.map(med => {
              const start = formatDate(med.startDate);
              const stop = formatDate(med.stopDate);

              return (
                <Grid item xs={12} md={6} key={med.id}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 1.5,
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      {med.drugName}{' '}
                      {med.drugStrength ? `• ${med.drugStrength}` : ''}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {med.doseForm}
                      {med.route ? ` • ${med.route}` : ''}
                    </Typography>

                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      sx={{ mb: 1 }}
                    >
                      {med.frequency && (
                        <Chip
                          icon={<ScheduleIcon fontSize="small" />}
                          size="small"
                          label={med.frequency}
                          variant="outlined"
                        />
                      )}
                      {med.quantity != null && (
                        <Chip
                          size="small"
                          label={`Qty ${med.quantity}`}
                          variant="outlined"
                        />
                      )}
                      {typeof med.refills === 'number' && (
                        <Chip
                          size="small"
                          label={`${med.refills} refills`}
                          variant="outlined"
                        />
                      )}
                      {med.status && (
                        <Chip
                          size="small"
                          label={med.status}
                          color="success"
                          variant="outlined"
                        />
                      )}
                    </Stack>

                    {med.doctor && (
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 0.5 }}
                      >
                        <LocalHospitalIcon fontSize="small" color="primary" />
                        <Typography variant="caption">
                          Prescriber: {med.doctor}
                        </Typography>
                      </Stack>
                    )}

                    {(start || stop) && (
                      <Typography variant="caption" color="text.secondary">
                        {start && `Start: ${start}`}
                        {start && stop && ' · '}
                        {stop && `Stop: ${stop}`}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* ========= MEDS HISTÓRICOS ========= */}
      {historicMeds.length > 0 && (
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 1,
              fontWeight: 600,
            }}
          >
            <HistoryIcon color="disabled" fontSize="small" />
            <Box ml={1}>Historic medications</Box>
          </Typography>

          <Grid container spacing={1}>
            {historicMeds.map(med => {
              const start = formatDate(med.startDate);
              const stop = formatDate(med.stopDate);

              return (
                <Grid item xs={12} md={6} key={med.id}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 1.5,
                      borderRadius: 1,
                      opacity: 0.85,
                    }}
                  >
                    <Typography variant="subtitle2">
                      {med.drugName}{' '}
                      {med.drugStrength ? `• ${med.drugStrength}` : ''}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 0.5 }}
                    >
                      {med.frequency || 'No frequency specified'}
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {med.status && (
                        <Chip
                          size="small"
                          label={med.status}
                          variant="outlined"
                        />
                      )}
                      {med.route && (
                        <Chip
                          size="small"
                          label={med.route}
                          variant="outlined"
                        />
                      )}
                    </Stack>

                    {(start || stop) && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, display: 'block' }}
                      >
                        {start && `Start: ${start}`}
                        {start && stop && ' · '}
                        {stop && `Stop: ${stop}`}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}
    </Paper>
  );
};

export default MedicationsSummary;
